import axios, { AxiosError, AxiosInstance } from 'axios';
import { format } from 'date-fns';
import { 
  connectResponseSchema, 
  createContactResponseSchema,
  submitApplicationResponseSchema,
  contactCenterAvailabilityResponseSchema,
  scheduleAppointmentResponseSchema,
  connectCallResponseSchema,
  availabilityResponseSchema,
  type ConnectResponse, 
  type CreateContactResponse,
  type SubmitApplicationResponse,
  type ContactCenterAvailabilityResponse,
  type ScheduleAppointmentResponse,
  type ConnectCallResponse,
  type AvailabilityResponse,
  ApiError, 
  successPageResponseSchema,
  type SuccessPageResponse
} from '../types/api';
import type { FormData } from '../types/form';
import { formatE164 } from '../utils/validation';

export interface ILeadsService {
  connectBySms(
    phoneNumber: string, 
    email: string, 
    dateOfBirth: string,
    loanAmount: number,
    loanPurpose: string,
    employmentStatus: string,
    employmentFrequency: string,
    educationLevel: string,
    annualIncome: number,
    propertyStatus: string,
    ipAddress: string,
    marketingParams: Record<string, string>
  ): Promise<ConnectResponse>;
  verifyCode(code: string, phoneNumber: string, spinwheelId: string, email: string, hubspotRecordId: string): Promise<ConnectResponse>;
  createContact(spinwheelId: string, email: string, hubspotRecordId: string): Promise<CreateContactResponse>;
  submitApplication(
    email: string,
    ssnLast4: string,
    loanAmount: number,
    loanPurpose: string,
    employmentStatus: string,
    employmentFrequency: string,
    educationLevel: string,
    annualIncome: number,
    propertyStatus: string,
    ipAddress: string
  ): Promise<SubmitApplicationResponse>;
  getContactCenterAvailability(): Promise<ContactCenterAvailabilityResponse>;
  scheduleAppointment(hubspotRecordId: string, meetingCallTime: string): Promise<ScheduleAppointmentResponse>;
  connectCall(hubspotRecordId: string): Promise<ConnectCallResponse>;
  getAvailability(date: string): Promise<AvailabilityResponse>;
  createContactExperian(
    email: string,
    ssn: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    phone?: string,
    dateOfBirth?: string
  ): Promise<CreateContactResponse>;
  // Track success page variant shown to customer
  successPage(hubspotRecordId: string, variantId: string): Promise<SuccessPageResponse>;
}

export class LeadsService implements ILeadsService {
  private readonly api: AxiosInstance;

  constructor(baseUrl: string, apiKey: string) {
    if (!baseUrl || !apiKey) {
      throw new Error('API configuration missing. Please check environment variables.');
    }

    this.api = axios.create({
      baseURL: baseUrl,
      headers: {
        'x-api-key': apiKey,
        'Content-Type': 'application/json'
      },
      timeout: 60000, // 60 second timeout
      validateStatus: status => status >= 200 && status < 300 // Only resolve for 2xx status codes
    });

    // Add response interceptor for global error handling
    this.api.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.data?.status) {
          throw new ApiError(
            error.response.data.status.message,
            error.response.data.status
          );
        }
        throw error;
      }
    );
  }

  async connectBySms(
    phoneNumber: string, 
    email: string, 
    dateOfBirth: string,
    loanAmount: number,
    loanPurpose: string,
    employmentStatus: string,
    employmentFrequency: string,
    educationLevel: string,
    annualIncome: number,
    propertyStatus: string,
    ipAddress: string,
    marketingParams: Record<string, string>
  ): Promise<ConnectResponse> {
    try {
      const response = await this.api.post('/leads/connect', {
        email,
        phone: formatE164(phoneNumber),
        dateOfBirth,
        loanAmount,
        loanPurpose,
        employmentStatus,
        employmentPayFrequency: (employmentFrequency === '') ? 'biweekly' : employmentFrequency,
        educationLevel,
        annualIncome,
        propertyStatus,
        ipAddress,
        marketingParams
      });

      return connectResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while connecting');
      }
      throw new Error('Failed to create connection');
    }
  }

  async verifyCode(
    code: string,
    phoneNumber: string,
    spinwheelId: string,
    email: string,
    hubspotRecordId: string
  ): Promise<ConnectResponse> {
    try {
      const response = await this.api.post('/leads/verify', {
        code,
        phoneNumber: formatE164(phoneNumber),
        spinwheelId,
        email,
        hubspotRecordId
      });

      return connectResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while verifying code');
      }
      throw new Error('Failed to verify code');
    }
  }

  async createContact(
    spinwheelId: string,
    email: string,
    hubspotRecordId: string
  ): Promise<CreateContactResponse> {
    try {
      const response = await this.api.post('/leads/create', {
        spinwheelId,
        email,
        hubspotRecordId
      });

      return createContactResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while creating contact');
      }
      throw new Error('Failed to create contact');
    }
  }

  async submitApplication(
    email: string,
    ssnLast4: string,
    loanAmount: number,
    loanPurpose: string,
    employmentStatus: string,
    employmentFrequency: string,
    educationLevel: string,
    annualIncome: number,
    propertyStatus: string,
    ipAddress: string
  ): Promise<SubmitApplicationResponse> {
    try {
      // Ensure SSN last 4 is exactly 4 digits with leading zeros preserved
      const formattedSsnLast4 = (ssnLast4 || '0000').padStart(4, '0').slice(-4);
      
      const response = await this.api.post('/leads/offers', {
        email,
        sensitiveLastFourSsn: formattedSsnLast4, // Keep as string to preserve leading zeros
        loanAmount,
        loanPurpose,
        employmentStatus,
        employmentPayFrequency: (employmentFrequency === '') ? 'biweekly' : employmentFrequency,
        educationLevel,
        annualIncome,
        propertyStatus,
        ipAddress
      });

      return submitApplicationResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while submitting application');
      }
      throw new Error('Failed to submit application');
    }
  }

  async getContactCenterAvailability(): Promise<ContactCenterAvailabilityResponse> {
    try {
      const response = await this.api.get('/leads/contact-center/availability');
      return contactCenterAvailabilityResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while checking availability');
      }
      throw new Error('Failed to check contact center availability');
    }
  }

  async scheduleAppointment(hubspotRecordId: string, meetingCallTime: string): Promise<ScheduleAppointmentResponse> {
    try {
      const response = await this.api.post(`/leads/${hubspotRecordId}/appointment`, {
        meetingCallTime
      });

      return scheduleAppointmentResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while scheduling appointment');
      }
      throw new Error('Failed to schedule appointment');
    }
  }

  async connectCall(hubspotRecordId: string): Promise<ConnectCallResponse> {
    try {
      const response = await this.api.post('/leads/call', {
        hubspotRecordId
      });

      return connectCallResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while connecting call');
      }
      throw new Error('Failed to connect call');
    }
  }

  async getAvailability(date: string): Promise<AvailabilityResponse> {
    try {
      // Ensure date is in yyyy-MM-dd format
      const formattedDate = this.formatDate(date);
      const response = await this.api.get(`/leads/availability?date=${formattedDate}`);
      return availabilityResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while fetching availability');
      }
      throw new Error('Failed to fetch availability');
    }
  }

  private formatDate(dateInput: string | Date): string {
    try {
      // If it's already a string and matches yyyy-MM-dd format, return as-is
      if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
        return dateInput;
      }
      
      // Convert to Date object and format
      const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date provided');
      }
      
      return format(date, 'yyyy-MM-dd');
    } catch (error) {
      throw new Error(`Invalid date format: ${dateInput}. Expected yyyy-MM-dd format.`);
    }
  }

  async createContactExperian(
    email: string,
    firstName: string,
    lastName: string,
    street: string,
    city: string,
    state: string,
    zip: string,
    dateOfBirth?: string,
    phone?: string,
    ssn?: string

  ): Promise<CreateContactResponse> {
    try {
      const payload = {
        ssn,
        personalInfo: {
          firstName,
          lastName,
          email,
          phone: phone || '',
          dateOfBirth: dateOfBirth || ''
        },
        addressInfo: {
          street,
          city,
          state,
          zipCode: zip
        }
      };

      const response = await this.api.post('/leads/create/experian', payload);
      return createContactResponseSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      if (error instanceof AxiosError) {
        throw new Error('Network error occurred while checking eligibility');
      }
      throw new Error('Failed to verify with backup provider');
    }
  }

  // Track success page variant shown to customer
  async successPage(hubspotRecordId: string, variantId: string): Promise<SuccessPageResponse> {
    try {
      // Backend expects 'availability' field with variant ID that includes availability prefix
      // e.g., 'high-availability-auto-call', 'low-outside-call-hours'
      const response = await this.api.post(`/leads/${hubspotRecordId}/thank-you`, { 
        availability: variantId 
      });
      return successPageResponseSchema.parse(response.data);
    } catch (error) {
      return { status: { code: 200, message: 'Success' } } as SuccessPageResponse;
    }
  }
}